<template>
  <AppShell :wide="true">
    <div class="agent-page">
      <section class="hero" aria-labelledby="agent-brand">
        <div class="hero-glow" aria-hidden="true" />
        <div class="hero-mesh" aria-hidden="true" />
        <div class="hero-inner">
          <div class="hero-copy">
            <p id="agent-brand" class="brand">算力百科 Agent</p>
            <h1 class="headline">本机多智能体工作台：拆任务、协作、交付成品</h1>
            <p class="lead">
              说出目标，总调度者安排专业智能体协作；一键试试查卡、选型、办公与创作任务。
            </p>
            <div class="cta-row">
              <a
                v-for="pkg in downloadOrder"
                :key="pkg.platform"
                class="cta"
                :class="[pkg.platform, { disabled: !pkg.available }]"
                :href="pkg.available ? pkg.url : undefined"
                :aria-disabled="pkg.available ? undefined : 'true'"
                @click="onDownloadClick(pkg, $event)"
              >
                <span class="cta-os">{{ pkg.platform === 'mac' ? 'macOS' : 'Windows' }}</span>
                <span class="cta-action">{{ pkg.available ? '立即下载' : '即将上线' }}</span>
                <span v-if="pkg.available && pkg.size_mb" class="cta-meta">{{ pkg.size_mb }} MB</span>
              </a>
            </div>
            <p class="cta-links">
              <a
                class="cta-doc"
                :href="releaseNotesUrl"
                target="_blank"
                rel="noopener noreferrer"
              >V1.0.0 版本说明</a>
            </p>
            <p v-if="catalogError" class="cta-hint warn">{{ catalogError }}</p>
            <p v-else class="cta-hint">安装后登录算力百科账号即可开始；成品保存在本机。</p>
            <details v-if="macAvailable" class="mac-open">
              <summary>macOS 首次打开提示</summary>
              <ol>
                <li>打开 `.dmg`，将应用拖到「应用程序」。</li>
                <li>若提示无法验证：在「应用程序」中 <strong>按住 Control 点应用 → 打开 → 仍要打开</strong>。</li>
                <li>或到「系统设置 → 隐私与安全性」找到拦截提示，点「仍要打开」。</li>
              </ol>
              <p>当前安装包尚未完成 Apple 公证，系统会提示一次；确认来自本站即可继续。</p>
            </details>
          </div>
          <figure class="hero-shot">
            <img
              src="/images/agent/shot-home.jpg"
              alt="算力百科 Agent 首页：总调度者与快速任务入口"
              width="1280"
              height="800"
              loading="eager"
            >
          </figure>
        </div>
      </section>

      <section class="block shots" aria-labelledby="shots-title">
        <header class="block-head">
          <p class="eyebrow">01 · 产品画面</p>
          <h2 id="shots-title">打开就能用的本地工作台</h2>
          <p>首页快速任务、智能体库与协作交付，一眼看清桌面版能做什么。</p>
        </header>
        <div class="shot-grid">
          <figure v-for="(shot, i) in shots" :key="shot.src" :style="{ '--i': i }">
            <img :src="shot.src" :alt="shot.alt" width="960" height="600" loading="lazy">
            <figcaption>
              <strong>{{ shot.title }}</strong>
              <span>{{ shot.body }}</span>
            </figcaption>
          </figure>
        </div>
      </section>

      <section class="block capabilities" aria-labelledby="cap-title">
        <header class="block-head featured">
          <p class="eyebrow">02 · 核心功能</p>
          <h2 id="cap-title">从一句话目标，到本机可交付成品</h2>
          <p>总调度者拆解任务、安排智能体；项目、定时、插件与资料都在本机。</p>
        </header>
        <ul class="cap-strip">
          <li v-for="(row, i) in workspace" :key="row.title" :style="{ '--i': i }">
            <strong>{{ row.title }}</strong>
            <span>{{ row.body }}</span>
          </li>
        </ul>
      </section>

      <section class="block agents-block" aria-labelledby="agents-title">
        <header class="block-head">
          <p class="eyebrow">03 · 内置智能体</p>
          <h2 id="agents-title">覆盖办公、研究、创作、工程与电商</h2>
          <p>按场景随时调度；算力选型由 ComputeAdvisor 对接网站查卡与估价能力。</p>
        </header>
        <div class="agent-grid">
          <article v-for="(agent, i) in agents" :key="agent.name" :style="{ '--i': i }">
            <h3>{{ agent.name }}</h3>
            <p>{{ agent.body }}</p>
          </article>
        </div>
      </section>

      <section class="block quick-block" aria-labelledby="quick-title">
        <header class="block-head">
          <p class="eyebrow">04 · 快速操作</p>
          <h2 id="quick-title">试试这些任务，点一下就能跑</h2>
          <p>与客户端首页一致：查卡、选型、办公交付，以及图片、视频、设计与开发。</p>
        </header>
        <ul class="quick-grid">
          <li v-for="(item, i) in quickActions" :key="item.title" :style="{ '--i': i }">
            <strong>{{ item.title }}</strong>
            <span>{{ item.desc }}</span>
            <em>交付：{{ item.deliver }}</em>
          </li>
        </ul>
      </section>
    </div>
  </AppShell>
</template>

<script>
import AppShell from '../layouts/AppShell.vue'
import { fetchAgentDownloads } from '../api/platformService'
import { setPageMeta, clearPageMeta } from '../utils/pageMeta'

const FALLBACK_PACKAGES = [
  { platform: 'mac', label: 'macos', available: false, url: '/api/downloads/agent/mac', size_mb: 0, filename: null },
  { platform: 'win', label: 'windows', available: false, url: '/api/downloads/agent/win', size_mb: 0, filename: null }
]

export default {
  name: 'AgentDesktop',
  components: { AppShell },
  data() {
    return {
      packages: FALLBACK_PACKAGES.slice(),
      catalogError: '',
      releaseNotesUrl: 'https://jiaolongcloud.feishu.cn/wiki/Gu9twPtydiunAsk8r1YcU01bnOe',
      shots: [
        {
          src: '/images/agent/shot-home.jpg',
          alt: '首页与快速任务',
          title: '首页快速任务',
          body: '查参数、选型、Word / PPT、图片与视频等入口一键发起。'
        },
        {
          src: '/images/agent/shot-agents.jpg',
          alt: '内置智能体库',
          title: '智能体库',
          body: '办公、研究、设计、开发、电商与算力选型顾问随时调度。'
        },
        {
          src: '/images/agent/shot-task.jpg',
          alt: '智能体交付标准与工作流程',
          title: '协作交付',
          body: '专业智能体按交付标准自检，查卡选型与成稿落在本机。'
        }
      ],
      workspace: [
        { title: '总调度者', body: '说出目标，自动拆解并安排多个智能体协作完成。' },
        { title: '项目与任务', body: '项目空间、任务历史与近期待办，上下文连续可跟进。' },
        { title: '定时任务', body: '把重复工作设为本地自动化，按频率在本机执行。' },
        { title: '连接器 / 插件', body: '扩展飞书、电商、办公等外部系统，按需授权调用。' },
        { title: '技能与应用', body: '技能包扩展能力；内置小应用辅助日常办公与创作。' },
        { title: '资料库', body: '长期资料与文件可加入任务，让智能体更懂你的上下文。' }
      ],
      agents: [
        { name: 'OfficeWorker', body: 'Word / Excel / PPT / PDF 读写、批处理与格式整理。' },
        { name: 'PptMaker', body: '从主题、提纲或文档生成可编辑演示文稿。' },
        { name: 'DeepResearcher', body: '多源深度调研与带引用的可复核报告。' },
        { name: 'ContentWriter', body: '社媒、博客、教程等可发布成稿与证据审校。' },
        { name: 'ImageStudio', body: '海报、封面、信息图等静态视觉制作修改。' },
        { name: 'VideoStudio', body: '解说、剪辑、字幕、竖屏与混音等视频成片。' },
        { name: 'VoiceStudio', body: '配音、朗读、转写字幕与音频混音。' },
        { name: 'UIDesigner', body: '界面与交互设计，默认 HTML 呈现。' },
        { name: 'ProductDeveloper', body: '需求落代码：实现、测试与评审。' },
        { name: 'SeoGeoAgent', body: 'SEO / GEO 诊断、行动清单与复测。' },
        { name: 'StockAnalyser', body: 'A / 港 / 美股研究、回测与组合风险。' },
        { name: 'ECommerceResearcher', body: '电商选品：需求、竞品与利润风险。' },
        { name: 'ECommerceAnalyzer', body: '经营复盘：利润、广告、退款与库存。' },
        { name: 'ECommerceWriter', body: '商品标题、卖点、详情与脚本文案。' },
        { name: 'ECommerceOperator', body: '上架、改价、促销与订单异常方案。' },
        { name: 'ECommerceReviewer', body: '评论 VOC、差评根因与改进优先级。' },
        { name: 'ComputeAdvisor', body: '算力选型：查卡、卡价、BOM 与选型结论。' }
      ],
      quickActions: [
        { title: '查参数', desc: '按厂商·显存·国产筛选规格', deliver: '规格清单' },
        { title: '卡价格', desc: '官方/市场参考价对照', deliver: '报价对照' },
        { title: '整机估价', desc: '组件清单·合计区间·说明', deliver: 'BOM 估价' },
        { title: '选型', desc: '场景约束·候选·推荐理由', deliver: '选型结论' },
        { title: 'Word', desc: '选型摘要·报价表·可编辑 Word', deliver: 'Word 报告' },
        { title: 'PPT', desc: '故事线·逐页设计·可编辑 PPT', deliver: 'PPT' },
        { title: '招投标', desc: '招标要点·符合性·可交付 PDF', deliver: 'PDF 报告' },
        { title: '图片', desc: '文案·排版·发布成品', deliver: '发布海报' },
        { title: '视频', desc: '脚本·分镜·字幕·成片', deliver: '视频成片' },
        { title: '设计', desc: '流程·状态·响应式页面', deliver: '登录页设计稿' },
        { title: '开发', desc: '响应式页面·表单·源码', deliver: '网站' },
        { title: '增长', desc: '关键词·内容·优先级', deliver: '增长方案' }
      ]
    }
  },
  computed: {
    downloadOrder() {
      const map = Object.fromEntries(this.packages.map((p) => [p.platform, p]))
      return ['mac', 'win'].map((key) => map[key] || FALLBACK_PACKAGES.find((p) => p.platform === key))
    },
    macAvailable() {
      const mac = this.downloadOrder.find((p) => p.platform === 'mac')
      return !!(mac && mac.available)
    }
  },
  async mounted() {
    setPageMeta({
      title: '算力百科 Agent · 桌面多智能体工作台',
      description:
        '下载算力百科 Agent：总调度者多智能体协作，内置办公/研究/创作/电商智能体，首页快速任务一键发起查卡、选型与成稿。',
      keywords: '算力百科,Agent,桌面版,多智能体,总调度者,智能体,快速任务,GPU选型',
      canonical: typeof window !== 'undefined' ? `${window.location.origin}/agent` : '/agent'
    })
    this.injectFonts()
    await this.loadCatalog()
  },
  beforeUnmount() {
    clearPageMeta()
  },
  methods: {
    injectFonts() {
      if (document.getElementById('agent-fonts')) return
      const link = document.createElement('link')
      link.id = 'agent-fonts'
      link.rel = 'stylesheet'
      link.href =
        'https://fonts.googleapis.com/css2?family=Outfit:wght@600;700;800&family=Noto+Sans+SC:wght@400;500;700&display=swap'
      document.head.appendChild(link)
    },
    async loadCatalog() {
      try {
        const data = await fetchAgentDownloads()
        if (Array.isArray(data.packages) && data.packages.length) {
          this.packages = data.packages
        }
        this.catalogError = ''
      } catch (e) {
        this.catalogError = '暂时无法读取安装包列表，请稍后刷新。'
      }
    },
    onDownloadClick(pkg, event) {
      if (!pkg.available) {
        event.preventDefault()
      }
    }
  }
}
</script>

<style scoped>
.agent-page {
  --ink: #142033;
  --muted: #5a667a;
  --line: rgba(20, 48, 90, 0.1);
  --blue: #165dff;
  --page: #eef3fa;
  --panel: rgba(255, 255, 255, 0.72);
  font-family: "Noto Sans SC", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  color: var(--ink);
  margin: -28px -16px -48px;
  padding-bottom: 64px;
  background:
    radial-gradient(900px 420px at 8% 18%, rgba(22, 93, 255, 0.08), transparent 60%),
    radial-gradient(700px 380px at 92% 42%, rgba(18, 184, 134, 0.07), transparent 55%),
    linear-gradient(180deg, #e8eef8 0%, #f4f7fc 38%, #eef3fa 100%);
}

.hero {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  padding: 56px 28px 48px;
  background:
    radial-gradient(1000px 480px at 10% 0%, rgba(61, 124, 255, 0.28), transparent 58%),
    radial-gradient(720px 360px at 100% 20%, rgba(18, 184, 134, 0.16), transparent 50%),
    linear-gradient(155deg, #1a3a7a 0%, #2456b8 45%, #2f6fe0 100%);
  color: #f7faff;
}

.hero-glow {
  position: absolute;
  inset: auto -8% -40% 40%;
  height: 58%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 62%);
  filter: blur(10px);
  animation: drift 12s ease-in-out infinite alternate;
  pointer-events: none;
}

.hero-mesh {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.07) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.5), transparent 88%);
  pointer-events: none;
}

.hero-inner {
  position: relative;
  z-index: 1;
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: 28px;
  align-items: end;
}

.hero-copy {
  max-width: 520px;
  animation: rise 0.8s ease both;
}

.brand {
  font-family: Outfit, "Noto Sans SC", sans-serif;
  font-weight: 800;
  font-size: clamp(2rem, 5vw, 3.1rem);
  letter-spacing: -0.03em;
  line-height: 1.05;
  margin: 0 0 14px;
}

.headline {
  font-family: Outfit, "Noto Sans SC", sans-serif;
  font-weight: 700;
  font-size: clamp(1.2rem, 2.3vw, 1.6rem);
  letter-spacing: -0.02em;
  line-height: 1.35;
  margin: 0 0 12px;
  color: rgba(255, 255, 255, 0.96);
}

.lead {
  margin: 0 0 24px;
  max-width: 34em;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(235, 242, 255, 0.86);
}

.hero-shot {
  margin: 0;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(8, 24, 64, 0.35);
  animation: rise 0.9s ease both;
  animation-delay: 80ms;
  transform: translateY(12px);
}
.hero-shot img {
  display: block;
  width: 100%;
  height: auto;
  vertical-align: top;
}

.cta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.cta {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  min-width: 168px;
  padding: 14px 18px;
  border-radius: 14px;
  text-decoration: none;
  color: #142033;
  background: #fff;
  box-shadow: 0 10px 28px rgba(16, 40, 90, 0.16);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.cta.mac { background: linear-gradient(180deg, #ffffff, #eef3ff); }
.cta.win { background: linear-gradient(180deg, #3d7cff, #165dff); color: #fff; }
.cta:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(16, 40, 90, 0.2);
}
.cta.disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.cta-os {
  font-family: Outfit, sans-serif;
  font-weight: 700;
  font-size: 15px;
}
.cta-action { font-size: 12px; opacity: 0.8; }
.cta-meta { font-size: 11px; opacity: 0.65; }

.cta-links {
  margin: 14px 0 0;
  font-size: 13px;
  color: rgba(230, 238, 255, 0.72);
}
.cta-doc {
  color: #fff;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.cta-doc:hover { color: #dce9ff; }

.cta-hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: rgba(230, 238, 255, 0.72);
}
.cta-hint.warn { color: #ffe0b8; }

.mac-open {
  margin-top: 14px;
  max-width: 36em;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(8, 24, 56, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(235, 242, 255, 0.88);
  font-size: 12px;
  line-height: 1.55;
}
.mac-open summary {
  cursor: pointer;
  font-weight: 700;
  color: #fff;
  list-style: none;
}
.mac-open summary::-webkit-details-marker { display: none; }
.mac-open summary::before {
  content: '›';
  display: inline-block;
  margin-right: 6px;
  transition: transform 0.15s ease;
}
.mac-open[open] summary::before { transform: rotate(90deg); }
.mac-open ol {
  margin: 10px 0 8px;
  padding-left: 1.2em;
}
.mac-open li + li { margin-top: 4px; }
.mac-open p {
  margin: 0;
  color: rgba(230, 238, 255, 0.7);
}

.block {
  padding: 52px 28px 12px;
  max-width: 1120px;
  margin: 0 auto;
}

.block-head {
  margin-bottom: 28px;
  max-width: 42em;
}
.block-head.featured h2 {
  font-size: clamp(1.55rem, 3vw, 2.05rem);
}
.eyebrow {
  margin: 0 0 8px;
  font-family: Outfit, sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--blue);
}
.block-head h2 {
  font-family: Outfit, "Noto Sans SC", sans-serif;
  font-size: clamp(1.35rem, 2.4vw, 1.8rem);
  letter-spacing: -0.02em;
  margin: 0 0 8px;
}
.block-head p {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.65;
}

.shot-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}
.shot-grid figure {
  margin: 0;
  animation: rise 0.65s ease both;
  animation-delay: calc(var(--i) * 70ms);
}
.shot-grid img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 12px 28px rgba(30, 60, 120, 0.1);
  background: #fff;
}
.shot-grid figcaption {
  display: grid;
  gap: 4px;
  margin-top: 12px;
}
.shot-grid strong {
  font-size: 14px;
}
.shot-grid span {
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.55;
}

.cap-strip {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  padding: 0;
  margin: 0;
}
.cap-strip li {
  padding: 18px 18px 16px;
  border-radius: 16px;
  background: var(--panel);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow: 0 8px 24px rgba(30, 60, 120, 0.06);
  display: grid;
  gap: 8px;
  animation: rise 0.65s ease both;
  animation-delay: calc(var(--i) * 60ms);
}
.cap-strip strong { font-size: 15px; }
.cap-strip span {
  color: var(--muted);
  font-size: 13px;
  line-height: 1.6;
}

.agents-block,
.quick-block {
  border-top: 1px solid var(--line);
  margin-top: 20px;
  padding-top: 48px;
}

.agent-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
}
.agent-grid article {
  padding: 16px 16px 16px 0;
  border-bottom: 1px solid var(--line);
  animation: rise 0.5s ease both;
  animation-delay: calc(var(--i) * 24ms);
}
.agent-grid h3 {
  margin: 0 0 6px;
  font-family: Outfit, "Noto Sans SC", sans-serif;
  font-size: 14px;
}
.agent-grid p {
  margin: 0;
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.55;
}

.quick-grid {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
}
.quick-grid li {
  display: grid;
  gap: 6px;
  padding: 16px 16px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.9);
  animation: rise 0.55s ease both;
  animation-delay: calc(var(--i) * 35ms);
}
.quick-grid strong {
  font-size: 14px;
}
.quick-grid span {
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.5;
}
.quick-grid em {
  font-style: normal;
  font-size: 11.5px;
  color: var(--blue);
  font-weight: 600;
}

@keyframes rise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes drift {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(-5%, -3%, 0); }
}

@media (max-width: 960px) {
  .hero-inner {
    grid-template-columns: 1fr;
    align-items: start;
  }
  .hero-shot { transform: none; }
  .shot-grid,
  .cap-strip,
  .agent-grid,
  .quick-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 720px) {
  .hero { padding: 44px 18px 36px; }
  .block { padding: 36px 18px 8px; }
  .shot-grid,
  .cap-strip,
  .agent-grid,
  .quick-grid {
    grid-template-columns: 1fr;
  }
  .agent-grid article { padding-right: 0; }
}
</style>
